import React, { useEffect, useMemo, useRef, useState } from "react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import PaymentTermsPicker from "./PaymentTermsPicker";

const EXPIRATION_VALUES = [
  { value: "15m", key: "bid.exp_15m" },
  { value: "30m", key: "bid.exp_30m" },
  { value: "1h", key: "bid.exp_1h" },
  { value: "2h", key: "bid.exp_2h" },
  { value: "3h", key: "bid.exp_3h" },
  { value: "6h", key: "bid.exp_6h" },
  { value: "12h", key: "bid.exp_12h" },
  { value: "24h", key: "bid.exp_24h" },
];

function normalizeExpiration(x) {
  const s = String(x || "").trim();
  return EXPIRATION_VALUES.some((v) => v.value === s) ? s : "1h";
}

function expLabel(value, t) {
  const x = EXPIRATION_VALUES.find((v) => v.value === value);
  if (!x) return String(value || "");

  const lang = String(i18next?.resolvedLanguage || i18next?.language || "ru")
    .toLowerCase()
    .split("-")[0];

  const RU_LABELS = {
    "15m": i18next.t("bidInline.duration.15m"),
    "30m": i18next.t("bidInline.duration.30m"),
    "1h": i18next.t("bidInline.duration.1h"),
    "2h": i18next.t("bidInline.duration.2h"),
    "3h": i18next.t("bidInline.duration.3h"),
    "6h": i18next.t("bidInline.duration.6h"),
    "12h": i18next.t("bidInline.duration.12h"),
    "24h": i18next.t("bidInline.duration.24h"),
  };

  const fallback = lang === "ru" ? (RU_LABELS[String(value)] || String(value)) : String(value);

  const key = x.key || "";
  try {
    return t
      ? t(key, { defaultValue: fallback })
      : (i18next?.t ? i18next.t(key, { defaultValue: fallback }) : fallback);
  } catch {
    return fallback;
  }
}

export default function BidInline({
  mode = "create", // create | edit
  initialPrice = "",
  initialExpiration = "1h",
  initialCurrency = "RUB",
  initialLoadType = "empty",
  initialPayment = null,

  priceLocked = false,
  isFixedPrice = false,

  onCancel,
  onSubmit,
  disabledReason = "",
}) {
  const { t } = useTranslation();

  const locked = !!priceLocked || !!isFixedPrice;

  const [value, setValue] = useState(initialPrice || "");
  const [expiration, setExpiration] = useState(normalizeExpiration(initialExpiration || "1h"));
  const [currency, setCurrency] = useState(String(initialCurrency || "RUB").toUpperCase());
  const [loadType, setLoadType] = useState(initialLoadType === "add_load" ? "add_load" : "empty");

  const [showExpSheet, setShowExpSheet] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toastTimer = useRef(null);
  const notifyFixedPrice = () => {
    setError(t("bid.fixed_price_order"));
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setError(""), 2000);
  };

  const [paymentOffer, setPaymentOffer] = useState(() => {
    const ip = initialPayment && typeof initialPayment === "object" ? initialPayment : null;
    return {
      bankWithVat: !!ip?.bankWithVat,
      bankNoVat: !!ip?.bankNoVat,
      cashTransfer: (ip ? !!ip?.cashTransfer : true),
      prepayPercent: (ip?.prepayPercent ?? 0),
      // MVP канон: две точки оплаты (при загрузке + остаток на выгрузке)
      payOnUnload: true,
      contractByMyCompany: false,
    };
  });

  useEffect(() => setValue(initialPrice || ""), [initialPrice]);
  useEffect(() => setExpiration(normalizeExpiration(initialExpiration || "1h")), [initialExpiration]);
  useEffect(() => setCurrency(String(initialCurrency || "RUB").toUpperCase()), [initialCurrency]);
  useEffect(() => setLoadType(initialLoadType === "add_load" ? "add_load" : "empty"), [initialLoadType]);

  useEffect(() => {
    const ip = initialPayment && typeof initialPayment === "object" ? initialPayment : null;
    if (!ip) return;
    setPaymentOffer({
      bankWithVat: !!ip.bankWithVat,
      bankNoVat: !!ip.bankNoVat,
      cashTransfer: !!ip.cashTransfer,
      prepayPercent: (ip.prepayPercent ?? 0),
      payOnUnload: true,
      contractByMyCompany: false,
    });
  }, [initialPayment]);

  const summary = useMemo(() => {
    const parts = [];
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) parts.push(`${Math.round(num)} ${currency==="USD"?"$":currency==="EUR"?"€":currency==="UZS"?"soʻm":"₽"}`);
    parts.push(`${i18next.t("bidInline.expirationPrefix")} ${expLabel(expiration, t)}`);
    return parts.join(" · ");
  }, [value, expiration]);

  const lastSubmitTouchAtRef = useRef(0);

  // Mobile/WebView sometimes does not fire "click" reliably; we handle touch explicitly.
  // Also защищаемся от bubbling (карточки/оверлеи) — останавливаем всплытие.
  const stopEvent = (e) => {
    try {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (e?.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
    } catch {}
  };

  const handleSubmit = async (e) => {
    stopEvent(e);
    setError("");

    if (disabledReason) {
      setError(disabledReason);
      return;
    }

    const base = (initialPrice !== "" && initialPrice !== null && initialPrice !== undefined) ? initialPrice : value;
    const num = Number(locked ? base : value);

    if (!num || num <= 0) {
      setError(t("bid.enter_positive"));
      return;
    }

    try {
      setLoading(true);

      const po = paymentOffer && typeof paymentOffer === "object" ? paymentOffer : {};

      // Front validation: backend rejects empty payment (invalid_payment)
      if (!po.bankWithVat && !po.bankNoVat && !po.cashTransfer) {
        setError(t("payment.choose_method"));
        return;
      }

      const ppRaw = po.prepayPercent;
      const ppNum = (ppRaw === null || ppRaw === undefined || ppRaw === "") ? 0 : Number(ppRaw);
      if (!Number.isFinite(ppNum) || ppNum < 0 || ppNum > 100) {
        setError(t("bid.prepay_0_100"));
        return;
      }

      const payment = {
        bankWithVat: !!po.bankWithVat,
        bankNoVat: !!po.bankNoVat,
        cashTransfer: !!po.cashTransfer,
        prepayPercent: Math.round(ppNum),
        payOnUnload: true,
        contractByMyCompany: false,
      };

      // fixed price order: driver must not change currency/price
      if (locked) {
        const fixedCur = String(initialCurrency || currency || "RUB").toUpperCase();
        await onSubmit?.(num, expiration, { loadType, payment, currency: fixedCur });
        return;
      }

      // non-fixed
      const cur = String(currency || "RUB").toUpperCase();
      await onSubmit?.(num, expiration, { loadType, payment, currency: cur });
    } catch (err) {
      setError(err?.message || i18next.t("bidInline.sendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTouch = (e) => {
    lastSubmitTouchAtRef.current = Date.now();
    handleSubmit(e);
  };

  const handleSubmitClick = (e) => {
    const dt = Date.now() - (lastSubmitTouchAtRef.current || 0);
    // ignore synthetic click right after touch
    if (dt >= 0 && dt < 700) return;
    handleSubmit(e);
  };

  const expirations = useMemo(() => {
    return EXPIRATION_VALUES.map((x) => ({
      ...x,
      label: expLabel(x.value, t),
    }));
  }, [t]);

  return (
    <>
      {/* Bottom-sheet срока действия */}
      {showExpSheet ? (
        <div className="fixed inset-0 z-[9999]">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowExpSheet(false)} />
          <div className="absolute left-0 right-0 bottom-0 rounded-t-2xl border border-white/10 bg-slate-950 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white font-semibold">{t("bid.expiration_title")}</div>
              <button
                type="button"
                className="text-white/70 hover:text-white text-sm"
                onClick={() => setShowExpSheet(false)}
              >
                {i18next.t("bidInline.close")}
              </button>
            </div>

            <div className="space-y-2">
              {expirations.map((x) => {
                const active = expiration === x.value;
                return (
                  <button
                    key={x.value}
                    type="button"
                    onClick={() => setExpiration(x.value)}
                    className={
                      "w-full flex items-center justify-between px-3 py-3 rounded-xl border text-left " +
                      (active
                        ? "bg-sky-600 border-sky-500 text-white"
                        : "bg-slate-900 border-slate-700 text-white/90 hover:bg-slate-800")
                    }
                  >
                    <span className="font-medium">{x.label}</span>
                    <span className={"text-sm " + (active ? "text-white" : "text-white/50")}>
                      {active ? i18next.t("bidInline.selected") : ""}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowExpSheet(false)}>
                {i18next.t("bidInline.done")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-2 p-3 rounded-xl border border-slate-700 bg-slate-900/40 space-y-4">
        <div className="text-sm font-semibold text-white/90">
          {mode === "edit" ? t("orders.actions.edit_offer") : t("orders.labels.your_offer")}
        </div>

        {mode !== "edit" ? (
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-100">
            {t("bid.accept_responsibility_warning", { defaultValue: "Если клиент выберет вас, подтвердите заказ вовремя. За неподтверждение или отказ до подтверждения будет списано 15 баллов Приоритета REISOR." })}
          </div>
        ) : null}

        {/* Цена */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-white/80">{t("bid.your_price_label")}</div>
            {locked ? (
              <button
                type="button"
                onClick={notifyFixedPrice}
                className="text-xs text-amber-300 hover:text-amber-200"
              >
                {t("bid.locked")}
              </button>
            ) : null}
          </div>

          <div className="flex gap-2 items-center w-full">
            <input
              type="number"
              inputMode="numeric"
              className={"flex-1 min-w-0 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white outline-none " + (locked ? "opacity-70" : "")}
              value={locked ? ((initialPrice !== "" && initialPrice !== null && initialPrice !== undefined) ? initialPrice : value) : value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t("common.example_12000")}
              disabled={locked || !!disabledReason}
            />

            {/* REISOR_DRIVER_BIDINLINE_CURRENCY_NATIVE_SAFE_SELECT_V1 */}
              <NativeSafeSelect
                className="shrink-0 w-24 min-w-[96px] max-w-[96px]"
                buttonClassName={"w-full px-2 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white outline-none text-left " + (locked ? "opacity-70" : "")}
                menuClassName="absolute right-0 z-[99999] mt-2 w-40 overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl"
                value={currency}
                onChange={(next) => setCurrency(String(next || "RUB").toUpperCase())}
                disabled={locked || !!disabledReason}
                ariaLabel="Currency"
                options={[
                  { value: "RUB", label: "₽ RUB" },
                  { value: "USD", label: "$ USD" },
                  { value: "EUR", label: "€ EUR" },
                  { value: "UZS", label: "soʻm UZS" },
                ]}
              />
          </div>

          <div className="text-xs text-white/50 mt-1">{summary}</div>
        </div>

        {/* Срок */}
        <div>
          <div className="text-sm text-white/80 mb-1">{t("bid.expiration_title")}</div>

          <button
            type="button"
            onClick={() => setShowExpSheet(true)}
            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-left hover:bg-slate-900"
          >
            {expLabel(expiration, t)}
          </button>
        </div>

        {/* Тип загрузки */}
        <div>
          <div className="text-sm text-white/80 mb-2">{t("load.type_title")}</div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLoadType("empty")}
              className={
                "px-3 py-2 rounded-xl text-sm border transition " +
                (loadType === "empty"
                  ? "bg-sky-600 border-sky-500 text-white"
                  : "bg-slate-900 border-slate-700 text-white/85 hover:bg-slate-800")
              }
            >
              {t("load.option_empty")}
            </button>

            <button
              type="button"
              onClick={() => setLoadType("add_load")}
              className={
                "px-3 py-2 rounded-xl text-sm border transition " +
                (loadType === "add_load"
                  ? "bg-sky-600 border-sky-500 text-white"
                  : "bg-slate-900 border-slate-700 text-white/85 hover:bg-slate-800")
              }
            >
              {t("load.option_add_load")}
            </button>
          </div>
        </div>

        {/* Условия оплаты */}
        <div className="pt-3 border-t border-white/10">
          <div className="text-sm text-white/80 mb-2">{t("payment.terms_title")}</div>
          <PaymentTermsPicker value={paymentOffer} onChange={setPaymentOffer} />
        </div>

        {disabledReason ? (
          <div className="rounded-xl border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-100">
            {/* REISOR_STAGE22P2Q8B_DRIVER_ACCESS_LIMITED_UI_I18N_V1 */}
            {disabledReason}
          </div>
        ) : null}

        {error ? <div className="text-sm text-red-400">{error}</div> : null}

        <div className="flex gap-2 justify-end pt-1">
          <Button type="button" variant="destructive" onClick={onCancel} disabled={loading || locked || !!disabledReason}>
            {t("buttons.cancel")}
          </Button>
          <Button type="button" disabled={loading} onTouchEnd={handleSubmitTouch} onClick={handleSubmitClick} className="touch-manipulation">{loading ? t("buttons.sending") : t("buttons.send")}</Button>
        </div>
      </form>
    </>
  );
}
